import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { socialApi } from '@/api/social.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
interface ApprovalRequest {
  id: number;
  ownerModule: string;
  entityType: string;
  entityId: number;
  approvalStatus: string;
}

export function SocialApprovalsPage() {
  const [items, setItems] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await socialApi.listPendingApprovals());
    } catch (e) {
      showApiError(e, 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id: number, approve: boolean) => {
    try {
      if (approve) await socialApi.approveApprovalRequest(id);
      else await socialApi.rejectApprovalRequest(id, 'Rejected from queue');
      notify.success(approve ? 'Approved' : 'Rejected');
      load();
    } catch (e) {
      showApiError(e, 'Action failed');
    }
  };

  const entityLink = (item: ApprovalRequest) => {
    if (item.entityType === 'SOCIAL_POST') return '/social/publishing';
    if (item.entityType === 'SOCIAL_CAMPAIGN') return '/social/campaigns';
    return '/social';
  };

  return (
    <div>
      <PageHeader
        title="Post approvals"
        description="Team queue for social posts and campaigns (SOCIAL_APPROVALS feature)"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Social', to: '/social' },
          { label: 'Approvals' },
        ]}
      />
      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      <ul className="card divide-y">
        {items.map((item) => (
          <li key={item.id} className="flex items-center justify-between p-4">
            <div>
              <p className="font-medium">
                <Link to={entityLink(item)} className="text-primary-600 hover:underline">
                  {item.entityType} #{item.entityId}
                </Link>
              </p>
              <p className="text-xs text-slate-500">{item.approvalStatus}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => void act(item.id, false)}>
                Reject
              </Button>
              <Button onClick={() => void act(item.id, true)}>Approve</Button>
            </div>
          </li>
        ))}
        {!loading && !items.length && (
          <li className="p-4 text-sm text-slate-500">No pending social approvals.</li>
        )}
      </ul>
    </div>
  );
}
